import React, { useState, useEffect } from 'react';
import {
    Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    Badge, IconButton, Switch, Modal, ModalOverlay, ModalContent,
    ModalHeader, ModalFooter, ModalBody, ModalCloseButton, FormControl,
    FormLabel, Input, Textarea, Grid, GridItem, Tabs, TabList, TabPanels,
    Tab, TabPanel, useDisclosure, useToast, useColorModeValue, Breadcrumb,
    BreadcrumbItem, BreadcrumbLink, InputGroup, InputLeftElement, AlertDialog,
    AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent,
    AlertDialogOverlay, HStack, Tooltip, Spinner, Avatar, AvatarBadge, Icon
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { MdEdit, MdDelete, MdAdd, MdStore, MdLocalShipping, MdEditNote } from 'react-icons/md';
import { FaPencilAlt } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../../axios-client';
import FloatingPlusButton from '../../../components/FloatingPlusButton';

export default function GestPlatformsAdmin() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const toast = useToast();

    // Tab index: 0 = E-commerce (APIType), 1 = Shipping (ShippingCompany), 2 = Modification (ModificationPlatform)
    const [tabIndex, setTabIndex] = useState(0);

    const [apiTypes, setApiTypes] = useState([]);
    const [shippingCompanies, setShippingCompanies] = useState([]);
    const [modifyPlatforms, setModifyPlatforms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modal state for Add/Edit
    const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: '',
        webhook_url_pattern: '',
        url: '',
        countries: '',
        is_active: true,
    });
    const [submitting, setSubmitting] = useState(false);

    // Alert dialog state for Delete
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const cancelRef = React.useRef();

    const cardBg = useColorModeValue('white', 'gray.700');
    const borderC = useColorModeValue('gray.200', 'gray.600');
    const hoverBg = useColorModeValue('gray.50', 'gray.600');
    const theadBg = useColorModeValue('gray.50', 'gray.800');

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [resApi, resShip, resMod] = await Promise.all([
                axiosClient.get('/api-types/?all=true').catch(() => ({ data: [] })),
                axiosClient.get('/shipping-companies/?all=true').catch(() => ({ data: [] })),
                axiosClient.get('/modification-platforms/?all=true').catch(() => ({ data: [] })),
            ]);
            setApiTypes(Array.isArray(resApi.data?.results || resApi.data) ? (resApi.data?.results || resApi.data) : []);
            setShippingCompanies(Array.isArray(resShip.data?.results || resShip.data) ? (resShip.data?.results || resShip.data) : []);
            setModifyPlatforms(Array.isArray(resMod.data?.results || resMod.data) ? (resMod.data?.results || resMod.data) : []);
        } catch (error) {
            toast({
                title: t('Erreur'),
                description: t("Erreur lors du chargement des plateformes"),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
        } finally {
            setLoading(false);
        }
    };

    const getEndpointForTab = (index) => {
        if (index === 0) return '/api-types';
        if (index === 1) return '/shipping-companies';
        return '/modification-platforms';
    };

    const getListForTab = (index) => {
        if (index === 0) return apiTypes;
        if (index === 1) return shippingCompanies;
        return modifyPlatforms;
    };

    const setListForTab = (index, updater) => {
        if (index === 0) setApiTypes(updater);
        else if (index === 1) setShippingCompanies(updater);
        else setModifyPlatforms(updater);
    };

    const handleOpenAddModal = () => {
        setIsEditing(false);
        setCurrentId(null);
        setSelectedImage(null);
        setImagePreview(null);
        setFormData({
            name: '',
            code: '',
            description: '',
            webhook_url_pattern: '',
            url: '',
            countries: '',
            is_active: true,
        });
        onModalOpen();
    };

    const handleOpenEditModal = (item) => {
        setIsEditing(true);
        setCurrentId(item.id);
        setSelectedImage(null);
        setImagePreview(item.logo || null);
        setFormData({
            name: item.name || '',
            code: item.code || '',
            description: item.description || '',
            webhook_url_pattern: item.webhook_url_pattern || '',
            url: item.url || '',
            countries: Array.isArray(item.countries) ? item.countries.join(', ') : (item.countries || ''),
            is_active: item.is_active ?? true,
        });
        onModalOpen();
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const endpoint = getEndpointForTab(tabIndex);
        
        let payload;
        if (selectedImage) {
            payload = new FormData();
            payload.append('name', formData.name);
            payload.append('code', formData.code.toLowerCase().replace(/\s+/g, '_'));
            payload.append('description', formData.description || '');
            payload.append('is_active', formData.is_active);
            payload.append('logo', selectedImage);

            if (tabIndex === 0) {
                payload.append('webhook_url_pattern', formData.webhook_url_pattern || '');
            } else if (tabIndex === 1) {
                payload.append('url', formData.url || '');
                const countriesArr = formData.countries.split(',').map((c) => c.trim()).filter(Boolean);
                payload.append('countries', JSON.stringify(countriesArr));
            }
        } else {
            payload = {
                name: formData.name,
                code: formData.code.toLowerCase().replace(/\s+/g, '_'),
                description: formData.description,
                is_active: formData.is_active,
            };
            if (tabIndex === 0) {
                payload.webhook_url_pattern = formData.webhook_url_pattern;
            } else if (tabIndex === 1) {
                payload.url = formData.url;
                payload.countries = formData.countries.split(',').map((c) => c.trim()).filter(Boolean);
            }
        }

        try {
            if (isEditing) {
                await axiosClient.patch(`${endpoint}/${currentId}/`, payload);
                toast({
                    title: t('Succès'),
                    description: t("Plateforme modifiée avec succès"),
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'bottom-right',
                });
            } else {
                await axiosClient.post(`${endpoint}/`, payload);
                toast({
                    title: t('Succès'),
                    description: t("Plateforme créée avec succès"),
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'bottom-right',
                });
            }
            onModalClose();
            fetchAllData();
        } catch (error) {
            toast({
                title: t('Erreur'),
                description: error.response?.data?.detail || t("Une erreur est survenue lors de l'enregistrement"),
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'bottom-right',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async (item) => {
        const newValue = !item.is_active;
        const endpoint = getEndpointForTab(tabIndex);

        // Optimistic update
        setListForTab(tabIndex, (prev) =>
            prev.map((i) => (i.id === item.id ? { ...i, is_active: newValue } : i))
        );

        try {
            await axiosClient.patch(`${endpoint}/${item.id}/`, { is_active: newValue });
            toast({
                title: t('Statut mis à jour'),
                status: 'success',
                duration: 2000,
                isClosable: true,
                position: 'bottom-right',
            });
        } catch (error) {
            // Revert on failure
            setListForTab(tabIndex, (prev) =>
                prev.map((i) => (i.id === item.id ? { ...i, is_active: !newValue } : i))
            );
            toast({
                title: t('Erreur'),
                description: t("Impossible de modifier le statut"),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
        }
    };

    const confirmDelete = (item) => {
        setItemToDelete(item);
        setIsAlertOpen(true);
    };

    const handleDelete = async () => {
        if (!itemToDelete) return;
        const endpoint = getEndpointForTab(tabIndex);
        try {
            await axiosClient.delete(`${endpoint}/${itemToDelete.id}/`);
            toast({
                title: t('Supprimé'),
                description: t("Plateforme supprimée avec succès"),
                status: 'info',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
            fetchAllData();
        } catch (error) {
            toast({
                title: t('Erreur'),
                description: t("Impossible de supprimer cette plateforme"),
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'bottom-right',
            });
        } finally {
            setIsAlertOpen(false);
            setItemToDelete(null);
        }
    };

    const currentList = getListForTab(tabIndex).filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getTabTitle = () => {
        if (tabIndex === 0) return t("APIs Sources de Commandes (E-commerce)");
        if (tabIndex === 1) return t("Sociétés de Livraison & COD");
        return t("Plateformes de Modification (Webhooks / Google Sheets)");
    };

    return (
        <Box position="relative" mb={5} p={{ base: 4, md: 6 }}>
            {/* Floating Plus Button */}
            <FloatingPlusButton tooltip={t("Ajouter une plateforme")} onClick={handleOpenAddModal} />

            {/* Breadcrumb */}
            <Flex mb={6}>
                <Breadcrumb fontStyle="normal" fontSize={{ base: "15px", sm: "16px", lg: "17px" }} color="gray.400">
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => navigate('/')}>{t("Accueil")}</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbItem isCurrentPage>
                        <BreadcrumbLink color="purple.500" fontWeight="semibold">{t("Plateformes & Intégrations")}</BreadcrumbLink>
                    </BreadcrumbItem>
                </Breadcrumb>
            </Flex>

            {/* Main Container */}
            <Flex
                px={6}
                py={6}
                bg={cardBg}
                rounded="2xl"
                direction="column"
                w="full"
                shadow="md"
                border="1px solid"
                borderColor={borderC}
            >
                {/* Header & Search */}
                <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
                    <Box>
                        <Text fontSize="2xl" fontWeight="bold" bgGradient="linear(to-r, purple.500, blue.500)" bgClip="text">
                            {t("Gestion des Plateformes SaaS")}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                            {t("Gérez le catalogue des APIs E-commerce, livreurs COD, et systèmes de modification.")}
                        </Text>
                    </Box>

                    <InputGroup size="md" w={{ base: 'full', md: '300px' }}>
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                            borderRadius="xl"
                            placeholder={t("Rechercher une plateforme...")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </InputGroup>
                </Flex>

                {/* Tabs */}
                <Tabs
                    variant="soft-rounded"
                    colorScheme="purple"
                    index={tabIndex}
                    onChange={(idx) => { setTabIndex(idx); setSearchTerm(''); }}
                    isLazy
                >
                    <TabList mb={6} flexWrap="wrap" gap={2}>
                        <Tab rounded="xl" py={2.5} px={4}>
                            <HStack spacing={2}>
                                <MdStore size={18} />
                                <Text fontWeight="semibold">{t("Sources E-commerce")}</Text>
                                <Badge colorScheme="purple" rounded="full">{apiTypes.length}</Badge>
                            </HStack>
                        </Tab>
                        <Tab rounded="xl" py={2.5} px={4}>
                            <HStack spacing={2}>
                                <MdLocalShipping size={18} />
                                <Text fontWeight="semibold">{t("Livraison / COD")}</Text>
                                <Badge colorScheme="blue" rounded="full">{shippingCompanies.length}</Badge>
                            </HStack>
                        </Tab>
                        <Tab rounded="xl" py={2.5} px={4}>
                            <HStack spacing={2}>
                                <MdEditNote size={18} />
                                <Text fontWeight="semibold">{t("Modification (Webhooks)")}</Text>
                                <Badge colorScheme="teal" rounded="full">{modifyPlatforms.length}</Badge>
                            </HStack>
                        </Tab>
                    </TabList>

                    <TabPanels>
                        {[0, 1, 2].map((idx) => (
                            <TabPanel key={idx} p={0}>
                                {loading ? (
                                    <Flex justify="center" align="center" py={12}>
                                        <Spinner size="xl" color="purple.500" thickness="4px" />
                                    </Flex>
                                ) : (
                                    <TableContainer overflowX="auto">
                                        <Table variant="simple" size="md">
                                            <Thead bg={theadBg}>
                                                <Tr>
                                                    <Th>{t("Plateforme & Code")}</Th>
                                                    <Th>{t("Description / Configuration")}</Th>
                                                    {idx === 1 && <Th>{t("Pays Supportés")}</Th>}
                                                    <Th textAlign="center">{t("Actif")}</Th>
                                                    <Th textAlign="center">{t("Actions")}</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {currentList.length === 0 ? (
                                                    <Tr>
                                                        <Td colSpan={idx === 1 ? 5 : 4} textAlign="center" py={8} color="gray.500">
                                                            {t("Aucune plateforme trouvée pour cette catégorie.")}
                                                        </Td>
                                                    </Tr>
                                                ) : (
                                                    currentList.map((item) => (
                                                        <Tr key={item.id} _hover={{ bg: hoverBg }} transition="0.2s">
                                                            <Td>
                                                                <HStack spacing={3}>
                                                                    {item.logo ? (
                                                                        <Avatar src={item.logo} name={item.name} size="sm" rounded="md" />
                                                                    ) : (
                                                                        <Avatar
                                                                            name={item.name}
                                                                            size="sm"
                                                                            rounded="md"
                                                                            bg={idx === 0 ? "purple.500" : idx === 1 ? "blue.500" : "teal.500"}
                                                                            color="white"
                                                                        />
                                                                    )}
                                                                    <Box>
                                                                        <Text fontWeight="bold" fontSize="md">{item.name}</Text>
                                                                        <Badge colorScheme="gray" fontFamily="mono" fontSize="2xs" rounded="md">
                                                                            {item.code}
                                                                        </Badge>
                                                                    </Box>
                                                                </HStack>
                                                            </Td>
                                                            <Td maxW="350px">
                                                                <Text fontSize="sm" color="gray.600" noOfLines={2}>
                                                                    {item.description || t("Aucune description fournie.")}
                                                                </Text>
                                                                {idx === 0 && item.webhook_url_pattern && (
                                                                    <Text fontSize="xs" color="purple.500" fontFamily="mono" mt={1} noOfLines={1}>
                                                                        URL: {item.webhook_url_pattern}
                                                                    </Text>
                                                                )}
                                                                {idx === 1 && item.url && (
                                                                    <Text fontSize="xs" color="blue.500" fontFamily="mono" mt={1} noOfLines={1}>
                                                                        API: {item.url}
                                                                    </Text>
                                                                )}
                                                            </Td>
                                                            {idx === 1 && (
                                                                <Td>
                                                                    <HStack flexWrap="wrap" gap={1}>
                                                                        {Array.isArray(item.countries) && item.countries.length > 0 ? (
                                                                            item.countries.map((c, i) => (
                                                                                <Badge key={i} colorScheme="teal" px={2} py={0.5} rounded="md">
                                                                                    {c}
                                                                                </Badge>
                                                                            ))
                                                                        ) : (
                                                                            <Text fontSize="xs" color="gray.400">{t("Global / Non spécifié")}</Text>
                                                                        )}
                                                                    </HStack>
                                                                </Td>
                                                            )}
                                                            <Td textAlign="center">
                                                                <Switch
                                                                    isChecked={item.is_active}
                                                                    onChange={() => handleToggleActive(item)}
                                                                    colorScheme="green"
                                                                />
                                                            </Td>
                                                            <Td textAlign="center">
                                                                <HStack justify="center" spacing={2}>
                                                                    <Tooltip label={t("Modifier")}>
                                                                        <IconButton
                                                                            icon={<MdEdit />}
                                                                            size="sm"
                                                                            colorScheme="blue"
                                                                            variant="ghost"
                                                                            onClick={() => handleOpenEditModal(item)}
                                                                        />
                                                                    </Tooltip>
                                                                    <Tooltip label={t("Supprimer")}>
                                                                        <IconButton
                                                                            icon={<MdDelete />}
                                                                            size="sm"
                                                                            colorScheme="red"
                                                                            variant="ghost"
                                                                            onClick={() => confirmDelete(item)}
                                                                        />
                                                                    </Tooltip>
                                                                </HStack>
                                                            </Td>
                                                        </Tr>
                                                    ))
                                                )}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </TabPanel>
                        ))}
                    </TabPanels>
                </Tabs>
            </Flex>

            {/* Modal Add / Edit */}
            <Modal isOpen={isModalOpen} onClose={onModalClose} size="lg" isCentered>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent rounded="2xl" p={2}>
                    <ModalHeader bgGradient="linear(to-r, purple.500, blue.500)" bgClip="text">
                        {isEditing ? t("Modifier la plateforme") : t("Ajouter une plateforme")}
                    </ModalHeader>
                    <ModalCloseButton />
                    <form onSubmit={handleSubmit}>
                        <ModalBody py={4}>
                            <Flex justify="center" align="center" mb={6} direction="column">
                                <Avatar
                                    src={imagePreview}
                                    name={formData.name || t("Plateforme")}
                                    size="xl"
                                    rounded="2xl"
                                    bg={tabIndex === 0 ? "purple.500" : tabIndex === 1 ? "blue.500" : "teal.500"}
                                    color="white"
                                    boxShadow="md"
                                >
                                    <label htmlFor="platformLogoInput">
                                        <input
                                            id="platformLogoInput"
                                            type="file"
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            onChange={handleImageChange}
                                        />
                                        <AvatarBadge
                                            cursor="pointer"
                                            rounded="full"
                                            bg="purple.600"
                                            boxSize="32px"
                                            border="2px solid white"
                                            _hover={{ bg: "purple.700" }}
                                            transition="0.2s"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Icon as={FaPencilAlt} color="white" boxSize="14px" />
                                        </AvatarBadge>
                                    </label>
                                </Avatar>
                                <Text fontSize="xs" color="gray.500" mt={2} fontWeight="medium">
                                    {t("Cliquez sur l'icône pour changer le logo")}
                                </Text>
                            </Flex>

                            <Grid templateColumns="1fr" gap={4}>
                                <GridItem>
                                    <FormControl isRequired>
                                        <FormLabel fontWeight="semibold">{t("Nom de la plateforme")}</FormLabel>
                                        <Input
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder={t("ex: WooCommerce, ShipBox, COD Network")}
                                            rounded="xl"
                                        />
                                    </FormControl>
                                </GridItem>

                                <GridItem>
                                    <FormControl isRequired>
                                        <FormLabel fontWeight="semibold">{t("Code unique (Identifiant API)")}</FormLabel>
                                        <Input
                                            name="code"
                                            value={formData.code}
                                            onChange={handleInputChange}
                                            placeholder={t("ex: woocommerce, shipbox, codnetwork")}
                                            rounded="xl"
                                            fontFamily="mono"
                                            isDisabled={isEditing}
                                        />
                                    </FormControl>
                                </GridItem>

                                <GridItem>
                                    <FormControl>
                                        <FormLabel fontWeight="semibold">{t("Description")}</FormLabel>
                                        <Textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            placeholder={t("Description courte des fonctionnalités et intégrations...")}
                                            rounded="xl"
                                            rows={3}
                                        />
                                    </FormControl>
                                </GridItem>

                                {tabIndex === 0 && (
                                    <GridItem>
                                        <FormControl>
                                            <FormLabel fontWeight="semibold">{t("Modèle d'URL Webhook (Optionnel)")}</FormLabel>
                                            <Input
                                                name="webhook_url_pattern"
                                                value={formData.webhook_url_pattern}
                                                onChange={handleInputChange}
                                                placeholder={t("ex: /api/woocommerce-webhook/{token}/")}
                                                rounded="xl"
                                                fontFamily="mono"
                                            />
                                        </FormControl>
                                    </GridItem>
                                )}

                                {tabIndex === 1 && (
                                    <>
                                        <GridItem>
                                            <FormControl>
                                                <FormLabel fontWeight="semibold">{t("Modèle d'URL API (Optionnel)")}</FormLabel>
                                                <Input
                                                    name="url"
                                                    value={formData.url}
                                                    onChange={handleInputChange}
                                                    placeholder={t("ex: https://api.shipbox.site/api/orders/create?key={key}")}
                                                    rounded="xl"
                                                    fontFamily="mono"
                                                />
                                            </FormControl>
                                        </GridItem>

                                        <GridItem>
                                            <FormControl>
                                                <FormLabel fontWeight="semibold">{t("Pays Supportés (séparés par une virgule)")}</FormLabel>
                                                <Input
                                                    name="countries"
                                                    value={formData.countries}
                                                    onChange={handleInputChange}
                                                    placeholder={t("ex: MA, SN, CI, TZ")}
                                                    rounded="xl"
                                                />
                                            </FormControl>
                                        </GridItem>
                                    </>
                                )}

                                <GridItem>
                                    <FormControl display="flex" alignItems="center" mt={3}>
                                        <FormLabel htmlFor="is_active" mb="0" fontWeight="semibold">
                                            {t("Plateforme Active")}
                                        </FormLabel>
                                        <Switch
                                            id="is_active"
                                            name="is_active"
                                            isChecked={formData.is_active}
                                            onChange={handleInputChange}
                                            colorScheme="green"
                                        />
                                    </FormControl>
                                </GridItem>
                            </Grid>
                        </ModalBody>
                        <ModalFooter gap={3}>
                            <Button variant="outline" rounded="xl" onClick={onModalClose}>
                                {t("Annuler")}
                            </Button>
                            <Button
                                type="submit"
                                colorScheme="purple"
                                rounded="xl"
                                isLoading={submitting}
                                leftIcon={<MdEdit />}
                            >
                                {isEditing ? t("Enregistrer") : t("Créer")}
                            </Button>
                        </ModalFooter>
                    </form>
                </ModalContent>
            </Modal>

            {/* Alert Dialog Delete */}
            <AlertDialog isOpen={isAlertOpen} leastDestructiveRef={cancelRef} onClose={() => setIsAlertOpen(false)}>
                <AlertDialogOverlay>
                    <AlertDialogContent rounded="2xl">
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {t("Supprimer la plateforme")} : {itemToDelete?.name}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {t("Attention ! La suppression de cette plateforme entraînera la suppression immédiate et irréversible de toutes les connexions actives et inactives des clients associés. Des notifications leur seront envoyées. Êtes-vous sûr de vouloir continuer ?")}
                        </AlertDialogBody>
                        <AlertDialogFooter gap={3}>
                            <Button ref={cancelRef} variant="outline" rounded="xl" onClick={() => setIsAlertOpen(false)}>
                                {t("Annuler")}
                            </Button>
                            <Button colorScheme="red" rounded="xl" onClick={handleDelete}>
                                {t("Supprimer")}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
}
